import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
  Sparkles, ArrowLeft, Check, Crown, Loader2, Star, Orbit, Moon,
  CreditCard, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import ShubhHeader from '../components/shubh/ShubhHeader';
import ShubhBottomNav from '../components/shubh/ShubhBottomNav';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

export const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const [currentTier, setCurrentTier] = useState('free');
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (user) {
      setCurrentTier(user.subscription_tier || 'free');
    }
  }, [user]);

  // Plans with translated text - YEARLY PLANS
  const getPlans = () => [
    {
      id: 'free',
      name: language === 'hi' ? 'मुफ्त' : 'Free',
      subtitle: language === 'hi' ? 'अपनी यात्रा शुरू करें' : 'Begin Your Journey',
      price: 0,
      period: language === 'hi' ? 'हमेशा' : 'forever',
      features: language === 'hi' 
        ? ['1 प्रोफाइल', 'प्रत्येक सुविधा 1 बार मुफ्त', 'बुनियादी भविष्यवाणी', 'शुभ रंग और अंक']
        : ['1 profile', 'Each feature free once', 'Basic predictions', 'Lucky color & number'],
      popular: false,
      icon: Star
    },
    {
      id: 'stellar',
      name: language === 'hi' ? 'स्टेलर' : 'Stellar',
      subtitle: language === 'hi' ? 'व्यक्तिगत उपयोग के लिए' : 'Perfect for Individuals',
      price: 99,
      period: language === 'hi' ? 'साल' : 'year',
      features: language === 'hi'
        ? ['2 प्रोफाइल', 'सभी सुविधाएं अनलॉक', 'दैनिक भविष्यवाणी', 'विस्तृत उपाय', 'प्राथमिकता सहायता']
        : ['2 profiles', 'All features unlocked', 'Daily predictions', 'Detailed remedies', 'Priority support'],
      popular: true,
      icon: Moon
    },
    {
      id: 'cosmic',
      name: language === 'hi' ? 'कॉस्मिक' : 'Cosmic',
      subtitle: language === 'hi' ? 'परिवार के लिए सर्वश्रेष्ठ' : 'Best for Families',
      price: 199,
      period: language === 'hi' ? 'साल' : 'year',
      features: language === 'hi'
        ? ['असीमित प्रोफाइल', 'सभी सुविधाएं अनलॉक', 'साप्ताहिक और मासिक पूर्वानुमान', 'रत्न सिफारिशें', 'व्यक्तिगत परामर्श', 'परिवार साझाकरण']
        : ['Unlimited profiles', 'All features unlocked', 'Weekly & Monthly forecasts', 'Gemstone recommendations', 'Personalized consultations', 'Family sharing'],
      popular: false,
      icon: Orbit
    }
  ];

  const plans = getPlans();

  const handlePayment = async (plan) => {
    if (plan.id === 'free' || plan.id === currentTier) return;
    
    setProcessing(plan.id);
    try {
      const orderResponse = await axios.post(
        `${API}/payment/razorpay/create-order`,
        { tier: plan.id },
        { withCredentials: true }
      );

      const { order_id, amount, key_id } = orderResponse.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "Nakshatra",
        description: `${plan.name} Plan - Yearly Subscription`,
        order_id: order_id,
        handler: async function (response) {
          try {
            await axios.post(
              `${API}/payment/razorpay/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                tier: plan.id
              },
              { withCredentials: true }
            );
            toast.success(`Welcome to ${plan.name}! Your cosmic journey expands.`);
            await refreshUser();
            setCurrentTier(plan.id);
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.mobile || ''
        },
        theme: {
          color: "#C84B32",
          backdrop_color: "#282622"
        },
        modal: {
          ondismiss: function() {
            setProcessing(null);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error('Payment failed. Please try again.');
        setProcessing(null);
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(null);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg overflow-x-hidden pb-28">
      <Toaster position="top-center" theme="dark" />
      
      {/* Background effects */}
      <div className="fixed inset-0 stars-bg opacity-30 pointer-events-none" />
      <div className="fixed inset-0 nebula-bg pointer-events-none" />

      {/* Navigation */}
      <ShubhHeader title="Plans" showBack />

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <p className="text-[#C84B32] text-xs uppercase tracking-[0.2em] mb-3">{language === 'hi' ? 'और अनलॉक करें' : 'Unlock More'}</p>
          <h1 className="font-['Fraunces'] text-3xl sm:text-4xl lg:text-5xl mb-4">
            <span className="gradient-text">{t('subscription.title')}</span>
          </h1>
          <p className="text-[#6B6560] text-sm sm:text-base max-w-md mx-auto">
            {t('subscription.subtitle')}
          </p>
        </div>

        {/* Current Plan Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#282622]/10 text-sm">
            <Crown className="w-4 h-4 text-[#C84B32]" />
            <span className="text-[#6B6560]">{t('subscription.currentPlan')}:</span>
            <span className="text-[#282622] font-medium capitalize">{plans.find(p => p.id === currentTier)?.name}</span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`subscription-card-${plan.id}`}
              className={`relative ${plan.popular ? 'pt-4' : ''}`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}
              <div className={`rounded-2xl sm:rounded-3xl p-5 sm:p-6 ${plan.popular ? 'pt-6' : ''} overflow-hidden ${
                plan.popular 
                  ? 'bg-gradient-to-b from-white to-[#FCE9D9] border-2 border-[#C84B32]/50' 
                  : 'bg-white border border-[#282622]/10'
              } ${
                currentTier === plan.id ? 'ring-2 ring-[#C84B32]' : ''
              }`}
              >

              <div className="pt-2">
                {/* Plan Icon & Name */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                    plan.popular ? 'bg-gradient-to-br from-[#C84B32] to-[#F5B942]' : 'bg-white'
                  }`}>
                    <plan.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${plan.popular ? 'text-white' : 'text-[#C84B32]'}`} />
                  </div>
                  <div>
                    <h3 className="font-['Fraunces'] text-xl sm:text-2xl text-[#282622]">
                      {plan.name}
                    </h3>
                    <p className="text-[#6B6560] text-xs sm:text-sm">{plan.subtitle}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-[#6B6560] text-sm">₹</span>
                  <span className="font-['Fraunces'] text-4xl sm:text-5xl text-[#282622]">
                    {plan.price}
                  </span>
                  <span className="text-[#6B6560] text-sm">/{plan.period}</span>
                </div>

                {/* Features */}
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2.5">
                      <div className="w-4 h-4 rounded-full bg-[#C84B32]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-2.5 h-2.5 text-[#C84B32]" />
                      </div>
                      <span className="text-[#6B6560] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                {currentTier === plan.id ? (
                  <Button
                    disabled
                    data-testid={`current-plan-${plan.id}`}
                    className="w-full py-5 sm:py-6 rounded-xl bg-[#FCE9D9]/60 border border-[#282622]/10 text-[#4A4640] font-semibold cursor-not-allowed disabled:opacity-100 text-sm sm:text-base"
                  >
                    Current Plan
                  </Button>
                ) : plan.id === 'free' ? (
                  <Button
                    disabled
                    className="w-full py-5 sm:py-6 rounded-xl bg-[#FCE9D9]/60 border border-[#282622]/10 text-[#4A4640] font-semibold cursor-not-allowed disabled:opacity-100 text-sm sm:text-base"
                  >
                    Free Forever
                  </Button>
                ) : (
                  <Button
                    onClick={() => handlePayment(plan)}
                    disabled={processing === plan.id}
                    data-testid={`upgrade-btn-${plan.id}`}
                    className={`w-full py-5 sm:py-6 rounded-xl font-medium text-sm sm:text-base ${
                      plan.popular
                        ? 'bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white hover:from-[#E85D43] hover:to-[#F5B942]'
                        : 'bg-transparent border border-[#C84B32]/50 text-[#C84B32] hover:bg-[#C84B32]/10'
                    }`}
                  >
                    {processing === plan.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe Now
                      </>
                    )}
                  </Button>
                )}
              </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust badges */}
        <div className="mt-10 flex flex-wrap justify-center gap-4 sm:gap-8 text-[#6B6560] text-xs sm:text-sm">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-600" />
            <span>256-bit encryption</span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C84B32]" />
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[#C84B32]" />
            <span>All payment methods</span>
          </div>
        </div>
      </main>
      <ShubhBottomNav />
    </div>
  );
};

export default SubscriptionPage;
